import React, { useState } from 'react';
import { Button, Form, Input, Modal, Card, Typography, Alert, Divider, Checkbox, message, Space } from 'antd';
import { UserOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone, SafetyOutlined, MailOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { getBaseURL } from "../config";

const { Title, Text } = Typography;

const Register = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const baseURL = getBaseURL();

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const response = await axios.post('http://localhost:8080/api/auth/register', {
                username: values.username,
                email: values.email,
                password: values.password
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                const { token, user } = response.data.data;
                localStorage.setItem('token', token);
                message.success(response.data.message || 'Registration successful!');
                navigate('/course-selection');
            } else {
                message.error(response.data.message || 'Registration failed');
            }
        } catch (error) {
            console.error('Registration error:', error);
            if (error.response?.data?.error) {
                const errorCode = error.response.data.error;
                const errorMessage = error.response.data.message;
                
                switch (errorCode) {
                    case 'USERNAME_EXISTS':
                        message.error('Username already exists');
                        break;
                    case 'EMAIL_EXISTS':
                        message.error('Email already exists');
                        break;
                    case 'VALIDATION_ERROR':
                        message.error('Please check your input data');
                        break;
                    default:
                        message.error(errorMessage || 'Registration failed');
                }
            } else {
                message.error('Network error. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const showAgreement = () => {
        Modal.info({
            title: '📋 User Agreement',
            width: 800,
            style: { top: 20 },
            content: (
                <div style={{ maxHeight: '60vh', overflow: 'auto', padding: '16px 0' }}>
                    <div style={{ marginBottom: 16 }}>
                        <Title level={4} style={{ color: '#1890ff', marginBottom: 8 }}>
                            1. Acceptance of Terms
                        </Title>
                        <Text>
                            By accessing and using the GalaxyMentor website, you accept and agree to be bound by the terms and provision of this agreement. In addition, when using the services provided by GalaxyMentor, you shall be subject to any posted guidelines or rules applicable to such services.
                        </Text>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <Title level={4} style={{ color: '#1890ff', marginBottom: 8 }}>
                            2. Modification of Terms
                        </Title>
                        <Text>
                            GalaxyMentor reserves the right to change, modify, or revise these terms at any time. Your continued use of the site will signify your acceptance of any adjustment to these terms.
                        </Text>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <Title level={4} style={{ color: '#1890ff', marginBottom: 8 }}>
                            3. User Registration
                        </Title>
                        <Text>
                            To access certain features of the GalaxyMentor site, you must register as a user. You agree to provide truthful and accurate information during the registration process.
                        </Text>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <Title level={4} style={{ color: '#1890ff', marginBottom: 8 }}>
                            4. Privacy Policy
                        </Title>
                        <Text>
                            Your privacy is critically important to us. Please refer to our Privacy Policy for information on how we collect, use, and disclose your personal information.
                        </Text>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <Title level={4} style={{ color: '#1890ff', marginBottom: 8 }}>
                            5. Conduct
                        </Title>
                        <Text>
                            You agree to act responsibly and to treat other users with respect. Any unauthorized use, including but not limited to, publishing, copying, or distributing any content is strictly prohibited.
                        </Text>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <Title level={4} style={{ color: '#1890ff', marginBottom: 8 }}>
                            6. Termination
                        </Title>
                        <Text>
                            GalaxyMentor has the right to terminate your access to the site, without any advance notice.
                        </Text>
                    </div>

                    <Alert
                        message="Agreement Acknowledgment"
                        description="By registering for an account on GalaxyMentor, you acknowledge that you have read and understood this Agreement, and agree to be bound by its terms and conditions."
                        type="info"
                        showIcon
                        style={{ marginTop: 16 }}
                    />
                </div>
            ),
            okText: 'I Understand',
            okButtonProps: { 
                style: { 
                    borderRadius: 8,
                    height: 40
                } 
            }
        });
    };

    if (success) {
        return (
            <div style={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
            }}>
                <Card
                    style={{
                        width: '100%',
                        maxWidth: 400,
                        borderRadius: 16,
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                        border: 'none',
                        textAlign: 'center'
                    }}
                    bodyStyle={{ padding: '40px' }}
                >
                    <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
                    <Title level={3} style={{ color: '#52c41a', marginBottom: 16 }}>
                        Registration Successful!
                    </Title>
                    <Text type="secondary" style={{ fontSize: 16 }}>
                        Your account has been created successfully. Redirecting to login page...
                    </Text>
                </Card>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
        }}>
            <Card
                style={{
                    width: '100%',
                    maxWidth: 450,
                    borderRadius: 16,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    border: 'none'
                }}
                bodyStyle={{ padding: '40px' }}
            >
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <Title level={2} style={{ marginBottom: 8, color: '#1890ff' }}>
                        🌟 Join Galaxy Mentor
                    </Title>
                    <Text type="secondary" style={{ fontSize: 16 }}>
                        Create your account to start learning
                    </Text>
                </div>

                {error && (
                    <Alert
                        message={error}
                        type="error"
                        showIcon
                        style={{ marginBottom: 24, borderRadius: 8 }}
                        closable
                        onClose={() => setError('')}
                    />
                )}

                <Form
                    form={form}
                    name="register"
                    onFinish={onFinish}
                    layout="vertical"
                    size="large"
                    scrollToFirstError
                >
                    <Form.Item
                        name="username"
                        rules={[
                            { required: true, message: 'Please enter your username!' },
                            { min: 3, message: 'Username must be at least 3 characters!' },
                            { max: 20, message: 'Username cannot exceed 20 characters!' },
                            { pattern: /^[a-zA-Z0-9_]+$/, message: 'Username can only contain letters, numbers, and underscores!' }
                        ]}
                    >
                        <Input
                            prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                            placeholder="Username"
                            style={{ borderRadius: 8, height: 48 }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        rules={[
                            { required: true, message: 'Please enter your email!' },
                            { type: 'email', message: 'Please enter a valid email address!' }
                        ]}
                    >
                        <Input
                            prefix={<MailOutlined style={{ color: '#1890ff' }} />}
                            placeholder="Email"
                            style={{ borderRadius: 8, height: 48 }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        rules={[
                            { required: true, message: 'Please enter your password!' },
                            { min: 8, message: 'Password must be at least 8 characters!' },
                            { pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number!' }
                        ]}
                        hasFeedback
                    >
                        <Input.Password
                            prefix={<LockOutlined style={{ color: '#1890ff' }} />}
                            placeholder="Password"
                            style={{ borderRadius: 8, height: 48 }}
                            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                        />
                    </Form.Item>

                    <Form.Item
                        name="confirm"
                        dependencies={['password']}
                        hasFeedback
                        rules={[
                            { required: true, message: 'Please confirm your password!' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('The two passwords do not match!'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password
                            prefix={<SafetyOutlined style={{ color: '#1890ff' }} />}
                            placeholder="Confirm Password"
                            style={{ borderRadius: 8, height: 48 }}
                            iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                        />
                    </Form.Item>

                    <Form.Item
                        name="agreement"
                        valuePropName="checked"
                        rules={[
                            {
                                validator: (_, value) =>
                                    value ? Promise.resolve() : Promise.reject(new Error('Please accept the agreement')),
                            },
                        ]}
                    >
                        <Checkbox style={{ fontSize: 14 }}>
                            I have read and agree to the{' '}
                            <Button type="link" onClick={showAgreement} style={{ padding: 0, height: 'auto' }}>
                                User Agreement
                            </Button>
                        </Checkbox>
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 16 }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            block
                            style={{
                                height: 48,
                                borderRadius: 8,
                                fontSize: 16,
                                fontWeight: 500
                            }}
                        >
                            {loading ? 'Creating Account...' : 'Create Account'}
                        </Button>
                    </Form.Item>
                </Form>

                <Divider style={{ margin: '24px 0' }}>
                    <Text type="secondary">Already have an account?</Text>
                </Divider>

                <div style={{ textAlign: 'center' }}>
                    <Link to="/login">
                        <Button
                            type="default"
                            block
                            style={{
                                height: 48,
                                borderRadius: 8,
                                fontSize: 16,
                                borderColor: '#1890ff',
                                color: '#1890ff'
                            }}
                        >
                            Sign In
                        </Button>
                    </Link>
                </div>
            </Card>
        </div>
    );
};

export default Register;
